/**
 * Bausteine der SuggestionCard: Warum-Zeile + ALT/NEU-Diff-Block.
 * Ausgelagert, damit die Card unter dem Component-Size-Limit bleibt.
 *
 * i18n (E4): `channelContent.card.why`/`missing` sind reale Keys. Für die
 * proaktive Erklärzeile fehlt ein eigener Key (T3 hat ihn nicht angelegt) —
 * bis dahin Substitut `channelContent.tabs.ready` (TODO im Bericht).
 */
import { AlertCircle, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TriggeredRule } from '@/lib/api/channel-content'

import { DV2 } from './tokens'

/** „Warum?"-Zeile — das Wichtigste auf der Karte (aus triggered_rules). */
export function WarumLine({
  rules,
  channelName,
}: {
  rules: TriggeredRule[]
  channelName: string
}) {
  const { t } = useTranslation()
  const proactive = !rules || rules.length === 0
  const r = rules?.[0]
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ${
        proactive ? `${DV2.line} ${DV2.card2} ${DV2.fg3}` : `${DV2.warnBd} ${DV2.warnBg} ${DV2.fg2}`
      }`}
    >
      <span className={proactive ? DV2.orange : DV2.warnFg}>
        {proactive ? <Sparkles size={14} /> : <AlertCircle size={14} />}
      </span>
      <span>
        <b className={DV2.fg1}>{t('channelContent.card.why')}</b>&nbsp;&nbsp;
        {proactive
          ? `${t('channelContent.card.proactiveHint')} — ${channelName}`
          : `${r.message}${r.actual ? ` — war ${r.actual}` : ''}${
              r.expected ? ` (erwartet: ${r.expected})` : ''
            }.`}
      </span>
    </div>
  )
}

/** Ein ALT- oder NEU-Diff-Block; Strings oder Bullet-Listen. */
export function DiffBlock({
  variant,
  label,
  value,
}: {
  variant: 'alt' | 'neu'
  label: string
  value: string | string[] | null
}) {
  const { t } = useTranslation()
  const isNeu = variant === 'neu'
  const rail = isNeu ? DV2.addRail : DV2.remRail
  const box = isNeu ? `${DV2.addBd} ${DV2.addBg} ${DV2.fg1}` : `${DV2.remBd} ${DV2.remBg} ${DV2.fg3}`
  const tag = isNeu ? DV2.addFg : DV2.remFg
  return (
    <div>
      <div className={`mb-1 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide ${tag}`}>
        <span>{label}</span>
      </div>
      <div className={`relative overflow-hidden rounded-lg border ${box} px-3 py-2.5 text-xs leading-relaxed`}>
        <span className={`absolute inset-y-0 left-0 w-0.5 ${rail}`} />
        {value === null ? (
          <span className="italic opacity-70">{t('channelContent.card.missing')}</span>
        ) : Array.isArray(value) ? (
          <ul className="space-y-1">
            {value.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className={`font-mono text-2xs ${tag}`}>{i + 1}</span>
                <span className={isNeu ? '' : 'line-through opacity-70'}>{b}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className={isNeu ? '' : 'line-through opacity-70'}>{value}</span>
        )}
      </div>
    </div>
  )
}
